import React from "react";
import { CardHeader, Box, Card, CardActions, CardContent, Grid, Stack, Typography, Button } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { PageContainer, SearchTextField } from "@packsize/components";
import { ApplicationLogo } from "@packsize/authentication";
import { orderBy } from "lodash";

import WalmartLogo from "@/assets/images/branding/walmart-logo.png";
import { FailedRetry } from "@/components/failed-retry";
import { Loading } from "@/components/loading";

import { NotificationBanner } from "./components/notification-banner";

interface KioskCardProps {
	readonly children: React.ReactNode;
	readonly actions?: React.ReactNode;
}

const KioskCard: React.FC<KioskCardProps> = ({ children, actions }) => {
	const intl = useIntl();

	return (
		<>
			<NotificationBanner />
			<PageContainer sx={{ height: "100vh", overflow: "hidden" }}>
				<Card
					sx={{
						flex: 1,
						display: "flex",
						flexDirection: "column",
					}}
				>
					<Stack
						direction="row"
						alignItems="center"
						justifyContent="space-between"
						gap={3}
						sx={{ height: 64, p: 2, borderBottom: 1, borderColor: "divider" }}
					>
						<Box
							component="img"
							src={WalmartLogo}
							alt={intl.formatMessage({ id: "Walmart Logo", defaultMessage: "Walmart Logo" })}
							sx={{ height: "100%", objectFit: "contain" }}
						/>

						<Typography variant="h1">
							<FormattedMessage id="Select Your Walmart Site" defaultMessage="Select Your Walmart Site" />
						</Typography>

						<Box
							role="img"
							aria-label={intl.formatMessage({ id: "Packsize Logo", defaultMessage: "Packsize Logo" })}
							sx={{ height: "100%", "& svg": { height: "100%", width: "auto" } }}
						>
							<ApplicationLogo variant="light" />
						</Box>
					</Stack>

					<Box sx={{ p: 2, overflow: "auto", flex: 1 }}>{children}</Box>

					{actions !== undefined && (
						<CardActions sx={{ justifyContent: "flex-end", borderTop: 1, borderColor: "divider", p: 2 }}>
							{actions}
						</CardActions>
					)}
				</Card>
			</PageContainer>
		</>
	);
};

export interface KioskSite {
	readonly id: string;
	readonly alias: string;
	readonly number: string;
	readonly redirect: string;
}

export interface KioskTemplateProps {
	readonly failed: boolean;
	readonly filteredList: readonly KioskSite[];
	readonly list: readonly KioskSite[];
	readonly loading: boolean;
	readonly onRetry: () => void;
	readonly onSearchTermChange: (value: string) => void;
	readonly searchTerm: string;
	readonly versionNumber: string;
}

export const KioskTemplate = ({
	failed,
	filteredList,
	list,
	loading,
	onRetry,
	onSearchTermChange,
	searchTerm,
	versionNumber,
}: KioskTemplateProps) => {
	const intl = useIntl();

	if (loading) {
		return (
			<KioskCard>
				<Loading />
			</KioskCard>
		);
	}

	if (failed) {
		return (
			<KioskCard>
				<FailedRetry retry={onRetry} />
			</KioskCard>
		);
	}

	return (
		<KioskCard
			actions={
				<SearchTextField
					onChange={onSearchTermChange}
					value={searchTerm}
					placeholder={intl.formatMessage({
						id: "Search for a site...",
						defaultMessage: "Search for a site...",
					})}
					sx={{ width: 300, maxWidth: "100%" }}
				/>
			}
		>
			{filteredList.length > 0 && (
				<Grid
					container
					gap={2}
					display="grid"
					gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr", lg: "repeat(4, 1fr)" }}
					sx={{ flex: 1, minWidth: 0, minHeight: 0, overflowY: "auto", overflowX: "hidden" }}
				>
					{orderBy(filteredList, (s) => s.alias).map((site) => (
						<Card
							key={site.id}
							onClick={() => window.location.replace(site.redirect)}
							sx={{ minWidth: 0, cursor: "pointer" }}
						>
							<CardHeader title={site.alias} subheader={site.number} />
							<CardContent>
								<Button fullWidth type="button">
									<FormattedMessage id="Select Site" defaultMessage="Select Site" />
								</Button>
							</CardContent>
						</Card>
					))}
				</Grid>
			)}

			{filteredList.length === 0 && (
				<Grid container alignItems="center" justifyContent="center" sx={{ height: 200 }}>
					<Typography variant="h2" sx={{ textAlign: "center" }}>
						{list.length > 0
							? intl.formatMessage({
									id: "No sites match your search",
									defaultMessage: "No sites match your search",
								})
							: intl.formatMessage({ id: "No sites configured", defaultMessage: "No sites configured" })}
					</Typography>
				</Grid>
			)}

			<Typography variant="caption" sx={{ position: "fixed", bottom: 8, left: 8 }}>
				<FormattedMessage
					id="Version: {version}"
					defaultMessage="Version: {version}"
					values={{ version: versionNumber }}
				/>
			</Typography>
		</KioskCard>
	);
};
